'use client';

import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import {
  UPZI_LOGIN_SUCCESS_EVENT_NAME,
  UPZI_LOGOUT_EVENT_NAME,
  UPZI_SESSION_EXPIRED_EVENT_NAME,
} from '@/common';
import { DeferredAuthModal } from '@/components/auth/deferred-auth-modal';
import { Button } from '@upzi/web-shell/ui/button';
import {
  getApplicantTrackingProperties,
  getQuickResponseTrackingProperties,
  getSaveOpportunityTrackingProperties,
  useOpportunityTracking,
  useTrackOpportunityDetail,
} from '@/hooks/use-opportunity-tracking';
import { pushApplyStart } from '@/lib/gtm';
import { checkApplicationExist } from '@/services/application';
import type { JobDetail } from '@/services/jobs/model';
import { useSavedJobs } from '@/services/jobs/use-saved-jobs';
import { OPPORTUNITY_TRACKING_EVENTS } from '@/services/opportunity-tracking';
import { SOURCE_SCREENS } from '@/services/opportunity-tracking/source-screen';
import type { JobShareUtmOverrides } from '../job-share';
import { isJobExpired } from '../utils';
import { LazyJobApplicationDialog } from './application-dialog/lazy-job-application-dialog';
import { JobSaveButton } from './job-save-button';
import { LazyJobSharePopover } from './lazy-job-share-popover';

const APPLY_BUTTON_CLASS =
  'font-body !text-ui-base !leading-5 h-10 w-full rounded-[14px] font-medium';
const APPLICATION_STATUS_PATH = '/quan-ly-trang-thai-ung-tuyen';

type JobDetailPageActionsProps = {
  isLoggedIn: boolean;
  job: JobDetail;
  shareUtm?: JobShareUtmOverrides;
};

export function JobDetailPageActions({
  isLoggedIn,
  job,
  shareUtm,
}: JobDetailPageActionsProps) {
  const { trackOpportunityEvent } = useOpportunityTracking();
  const [clientAuthOverride, setClientAuthOverride] = useState<boolean | null>(
    null
  );
  const [appliedState, setAppliedState] = useState({
    isApplied: false,
    jobId: job.id,
  });
  const isAuthenticated = clientAuthOverride ?? isLoggedIn;

  useTrackOpportunityDetail(job, 'page', isAuthenticated);
  const isApplied =
    isAuthenticated && appliedState.jobId === job.id && appliedState.isApplied;
  const isExpired = isJobExpired(job);
  const { checkSavedJobs, pendingJobId, savedJobIds, toggleSavedJob } =
    useSavedJobs({
      enabled: isAuthenticated,
      fetchOnMount: false,
      sourceScreen: SOURCE_SCREENS.JOB_DETAIL,
    });

  useEffect(() => {
    const handleLoginSuccess = () => setClientAuthOverride(true);
    const handleLogout = () => setClientAuthOverride(false);

    window.addEventListener(UPZI_LOGIN_SUCCESS_EVENT_NAME, handleLoginSuccess);
    window.addEventListener(UPZI_LOGOUT_EVENT_NAME, handleLogout);
    window.addEventListener(UPZI_SESSION_EXPIRED_EVENT_NAME, handleLogout);

    return () => {
      window.removeEventListener(
        UPZI_LOGIN_SUCCESS_EVENT_NAME,
        handleLoginSuccess
      );
      window.removeEventListener(UPZI_LOGOUT_EVENT_NAME, handleLogout);
      window.removeEventListener(UPZI_SESSION_EXPIRED_EVENT_NAME, handleLogout);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    void checkSavedJobs([job.id]);
  }, [checkSavedJobs, isAuthenticated, job.id]);

  useEffect(() => {
    if (!isAuthenticated || !job.id) return;

    const controller = new AbortController();

    void checkApplicationExist(job.id, controller.signal)
      .then(response => {
        if (!controller.signal.aborted) {
          setAppliedState({
            isApplied: response.data?.isApplied === true,
            jobId: job.id,
          });
        }
      })
      .catch(error => {
        if (!controller.signal.aborted) {
          console.error(
            '[jobs/application] Unable to check application status',
            error
          );
        }
      });

    return () => controller.abort();
  }, [isAuthenticated, job.id]);

  const handleSaveChange = useCallback(() => {
    void toggleSavedJob(job);
  }, [job, toggleSavedJob]);

  const handleGuestApply = useCallback(() => {
    pushApplyStart(job, 'job_detail');

    return trackOpportunityEvent({
      eventName: OPPORTUNITY_TRACKING_EVENTS.applyStart,
      includeUserInfo: false,
      jobId: job.id,
      properties: {
        ...getApplicantTrackingProperties(job),
        ...getQuickResponseTrackingProperties(job),
        opportunity_type: 'job',
        source_screen: SOURCE_SCREENS.JOB_DETAIL,
      },
    });
  }, [job, trackOpportunityEvent]);

  const handleGuestSaveChange = useCallback(() => {
    return trackOpportunityEvent({
      eventName: OPPORTUNITY_TRACKING_EVENTS.save,
      includeUserInfo: false,
      jobId: job.id,
      properties: getSaveOpportunityTrackingProperties(
        SOURCE_SCREENS.JOB_DETAIL
      ),
    });
  }, [job.id, trackOpportunityEvent]);

  const applicationDialog = (
    <LazyJobApplicationDialog
      job={job}
      jobId={job.id}
      jobTitle={job.jobTitle}
      onApplied={() => setAppliedState({ isApplied: true, jobId: job.id })}
      sourceScreen={SOURCE_SCREENS.JOB_DETAIL}
      trigger={
        <Button type="button" className={APPLY_BUTTON_CLASS}>
          Ứng tuyển
        </Button>
      }
    />
  );
  const applyButton = isApplied ? (
    <Button asChild className={APPLY_BUTTON_CLASS}>
      <Link
        href={APPLICATION_STATUS_PATH}
        target="_blank"
        rel="noopener noreferrer"
      >
        Xem trạng thái ứng tuyển
      </Link>
    </Button>
  ) : isExpired ? (
    <Button type="button" disabled className={APPLY_BUTTON_CLASS}>
      Ứng tuyển
    </Button>
  ) : isAuthenticated ? (
    applicationDialog
  ) : (
    <DeferredAuthModal
      authenticatedContent={applicationDialog}
      triggerButton={
        <Button
          type="button"
          className={APPLY_BUTTON_CLASS}
          onClick={handleGuestApply}
        >
          Ứng tuyển
        </Button>
      }
    />
  );

  return (
    <div className="grid h-10 grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-3">
      <div className="min-w-0">{applyButton}</div>
      <div className="min-w-0 [&>span]:w-full">
        <JobSaveButton
          variant="outline"
          className="border-base-border bg-surface-pure text-on-surface hover:bg-primary-variant h-10 w-full rounded-xl shadow-none"
          isPending={pendingJobId === job.id}
          isSaved={savedJobIds.has(job.id)}
          onSaveChange={
            isAuthenticated ? handleSaveChange : handleGuestSaveChange
          }
          requiresLogin={!isAuthenticated}
          showLabel
        />
      </div>
      <LazyJobSharePopover
        job={job}
        shareUtm={shareUtm}
        triggerClassName="border-base-border size-10 rounded-xl"
      />
    </div>
  );
}
